<template>
    <v-card>
      <v-card-title>
        <edit-label-dialog @update_label="update_label" :labels="dialogLabels" :item="editedItem" @saved="onLabelSaved" @merged="onLabelMerged" @close_modal="close_modal" v-if="dialog"></edit-label-dialog>
      </v-card-title>
      <div v-if="totalItems > 0 || hasActiveFilters" id="labels_total_count" class="labels-total px-4 pb-2">
        {{
          hasActiveFilters
            ? $t('set_labels.filtered_count', { n: totalItems })
            : $t('set_labels.total_count', { n: totalItems })
        }}
      </div>
      <div id="labels_hierarchy_filter" class="labels-hierarchy-filter px-4 pb-3">
        <div class="labels-hierarchy-filter__autocomplete">
          <search-label-autocomplete
            id="labels_list_ancestor_search"
            :clear_on_select="false"
            :track_last_used="false"
            :field_label="$t('set_labels.list_view_under_label')"
            :placeholder="$t('set_labels.list_view_under_label_placeholder')"
            @label-selected="onAncestorSelected"
            @cleared="onAncestorCleared"
          ></search-label-autocomplete>
        </div>
        <v-checkbox
          id="labels_list_direct_children_only"
          v-model="directChildrenOnly"
          :label="$t('set_labels.list_view_direct_children_only')"
          hide-details
          density="compact"
          class="labels-hierarchy-filter__checkbox"
          :disabled="!ancestorId"
        ></v-checkbox>
      </div>
      <v-data-table-server
        v-model="selected_labels"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        v-model:sort-by="sortBy"
        item-value="id"
        return-object
        :loading="loading"
        :show-select="show_select"
        :items="tableItems"
        :items-length="totalItems"
        :headers="table_headers"
        @update:options="onOptionsUpdate"
      >
        <template v-slot:body.prepend>
          <tr>
            <td v-if="show_select"></td>
            <td></td>
            <td>
              <v-text-field
                id="labels_list_search"
                append-icon="mdi-magnify"
                single-line
                v-model="search"
                type="text"
                :label="$t('set_labels.list_view_search')"
                hide-details
                density="compact"
              ></v-text-field>
            </td>
            <td>
              <v-text-field
                id="labels_list_parent_search"
                append-icon="mdi-magnify"
                single-line
                v-model="parentSearch"
                type="text"
                :label="$t('set_labels.list_view_parent_search')"
                hide-details
                density="compact"
              ></v-text-field>
            </td>
            <td></td>
            <td>
              <v-text-field
                append-icon="mdi-magnify"
                single-line
                v-model="greater_than"
                type="text"
                :label="$t('set_labels.list_view_greater')"
                hide-details
                density="compact"
              ></v-text-field>
            </td>
            <td v-if="menu_items_on_select.length > 0" style="text-align: right;">
              <dropdown-menu v-if="selected_labels.length > 0" :menu_items="menu_items_on_select"
                  ></dropdown-menu>
            </td>
          </tr>
        </template>
        <template v-slot:item.actions="{ item }">

          <div :style="{'backgroundColor': item.rgb_color}" style="height: 18px; width: 18px; float: left; margin-right: 5px; margin-top: 3px;"></div>
          <v-icon
            small
            class="mr-2"
            @click="editItem(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
              small
              class="mr-2"
              @click="preview(item.name)"
          >
            fa-table-cells-large
          </v-icon>
          <v-icon
              v-if="item.count === 0"
              small
              class="mr-2"
              @click="delete_label(item)"
          >
            far fa-trash-alt
          </v-icon>
        </template>
      </v-data-table-server>
    </v-card>
</template>

<script>
import EditLabelDialog from "@/components/manage_labels/EditLabelDialog";
import EditLabelMixin from "@/components/manage_labels/EditLabelMixin";
import DropdownMenu from "@/components/layout/DropdownMenu";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";
import { vuetifyHeaders } from "@/utils/vuetifyHeaders";

export default {
  name: "ListView",
  data() {
    return {
      search: '',
      parentSearch: '',
      ancestorId: null,
      directChildrenOnly: false,
      greater_than: '',
      selected_labels: [],
      page: 1,
      itemsPerPage: 50,
      sortBy: [{ key: 'name', order: 'asc' }],
      loading: false,
      serverItems: [],
      totalItems: 0,
      searchDebounce: null,
      optionsSeq: 0,
      /** True while applying selected_labels_ids → table v-model (avoid wipe feedback). */
      syncingSelection: false,
    }
  },
  watch: {
    selected_labels(n) {
      if (this.syncingSelection) {
        return;
      }
      const ids = (n || []).map((item) =>
        typeof item === 'object' && item != null ? item.id : item
      ).filter((id) => id != null).map(Number);
      // Merge with selections on other pages (server table only knows current page rows).
      const pageIds = new Set((this.serverItems || []).map((l) => Number(l.id)));
      const kept = (this.selected_labels_ids || [])
        .map(Number)
        .filter((id) => !pageIds.has(id));
      const merged = [...new Set([...kept, ...ids])];
      const current = (this.selected_labels_ids || []).map(Number);
      const same =
        merged.length === current.length
        && merged.every((id) => current.includes(id));
      if (!same) {
        this.$emit('select_label_ids', merged);
      }
    },
    selected_labels_ids() {
      this.syncSelectedLabels();
    },
    // tableItems are object copies; re-sync when the visible page set changes.
    tableItems() {
      this.syncSelectedLabels();
    },
    search() {
      this.scheduleFetch();
    },
    parentSearch() {
      this.scheduleFetch();
    },
    directChildrenOnly() {
      if (this.ancestorId) {
        this.page = 1;
        this.fetchPage();
      }
    },
    greater_than() {
      // Client-side filter on the current page only (stats threshold).
    },
  },
  props: {
    selected_labels_ids: {default: () => []},
    show_select: {default: false},
    labels: {default: () => []},
    show_header: {default: true},
    parent: {default: false},
    menu_items_on_select: {default: () => []},
  },
  components: {EditLabelDialog, DropdownMenu, SearchLabelAutocomplete},

  computed: {
    hasActiveFilters() {
      return !!(
        (this.search || '').trim()
        || (this.parentSearch || '').trim()
        || this.ancestorId
      );
    },
    dialogLabels() {
      return this.serverItems;
    },
    tableItems() {
      return this.extendedPageItems.filter((label) => {
        if (!this.greater_than) {
          return true;
        }
        const threshold = Number(this.greater_than);
        if (!Number.isFinite(threshold)) {
          return true;
        }
        return label.count > threshold;
      });
    },
    extendedPageItems() {
      return (this.serverItems || []).map((raw) => {
        const l = { ...raw };
        l.rgb_color = 'rgb(' + l.color + ')';
        if (l.relationships && l.relationships.annotation_stats && l.relationships.annotation_stats.length > 0) {
          const processed = l.relationships.annotation_stats.map((stats) => stats.processed_count);
          const sum = processed.reduce((a, b) => a + b, 0);
          l.stats = sum;
          l.count = sum;
        } else {
          l.stats = 'N';
          l.count = 0;
        }
        if (!l.parent) {
          l.parent = '';
        }
        l.type_name = l.type === 2 ? 'Point' : 'Box';
        return l;
      });
    },
    table_headers() {
      return vuetifyHeaders([
        { text: this.$t('set_labels.list_view_id'), value: 'id' },
        { text: this.$t('set_labels.list_view_name'), value: 'name' },
        { text: this.$t('set_labels.list_view_parent'), value: 'parent' },
        { text: this.$t('set_labels.list_view_type'), value: 'type_name' },
        { text: this.$t('set_labels.list_view_stats'), value: 'stats' },
        { text: this.$t('set_labels.list_view_actions'), value: 'actions', sortable: false },
      ]);
    },
  },
  methods: {
    scheduleFetch() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {
        this.page = 1;
        this.fetchPage();
      }, 300);
    },
    onAncestorSelected(label) {
      const id = label && label.id != null ? Number(label.id) : null;
      if (!id || Number.isNaN(id)) {
        return;
      }
      this.ancestorId = id;
      this.page = 1;
      this.fetchPage();
    },
    onAncestorCleared() {
      this.ancestorId = null;
      this.page = 1;
      this.fetchPage();
    },
    syncSelectedLabels() {
      if (!this.show_select) {
        return;
      }
      // Must use the same row objects as :items (tableItems). With return-object,
      // selecting serverItems copies leaves every checkbox unchecked in the edit modal.
      const selectedSet = new Set((this.selected_labels_ids || []).map(Number));
      const next = (this.tableItems || []).filter((label) =>
        selectedSet.has(Number(label.id)),
      );
      const prev = this.selected_labels || [];
      // Re-bind when row object identity changed (extendedPageItems rebuilds copies).
      const sameRefs =
        prev.length === next.length
        && next.every((label, i) => label === prev[i]);
      if (sameRefs) {
        return;
      }
      this.syncingSelection = true;
      this.selected_labels = next;
      this.$nextTick(() => {
        this.syncingSelection = false;
      });
    },
    async onOptionsUpdate(options) {
      if (options) {
        if (options.page != null) {
          this.page = options.page;
        }
        if (options.itemsPerPage != null) {
          this.itemsPerPage = options.itemsPerPage;
        }
        if (options.sortBy) {
          this.sortBy = options.sortBy;
        }
      }
      await this.fetchPage();
    },
    async fetchPage() {
      const seq = ++this.optionsSeq;
      this.loading = true;
      try {
        const sort = (this.sortBy && this.sortBy[0]) || { key: 'name', order: 'asc' };
        const sortKey = sort.key === 'type_name' ? 'type' : (sort.key || 'name');
        const res = await this.$api.label.fetchV2Page({
          page: this.page || 1,
          itemsPerPage: this.itemsPerPage || 50,
          filter: (this.search || '').trim(),
          parentFilter: (this.parentSearch || '').trim(),
          ancestorId: this.ancestorId,
          directChildrenOnly: !!this.directChildrenOnly,
          includeAncestor: false,
          sortBy: ['name', 'id', 'type'].includes(sortKey) ? sortKey : 'name',
          sortDesc: sort.order === 'desc',
        });
        if (seq !== this.optionsSeq) {
          return;
        }
        const items = (res && res.items) || [];
        this.serverItems = items;
        this.totalItems = (res && typeof res.total === 'number') ? res.total : items.length;
        this.$store.commit('label/SET_TOTAL_COUNT', this.totalItems);
        this.syncSelectedLabels();
        this.$emit('page_loaded', { total: this.totalItems, items: this.serverItems });
      } catch (e) {
        console.error(e);
        if (seq === this.optionsSeq) {
          this.serverItems = [];
          this.totalItems = 0;
        }
      } finally {
        if (seq === this.optionsSeq) {
          this.loading = false;
        }
      }
    },
    async delete_label(label) {
      this.loading = true
      await this.$store.dispatch('label/delete', label)
      await this.$store.dispatch('snackbars/add_success_snack', {
          'message': this.$t('snacks.label_deleted'),
        })
      await this.fetchPage();
      this.loading = false
    },
    async onLabelSaved() {
      await this.fetchPage();
    },
    async onLabelMerged() {
      await this.fetchPage();
    },
    preview(label_name) {
      this.$emit('preview_label', label_name)
    },
    add(a, b) {
      return a + b
    },
    /** Public: parent can ask list to reload after import/add. */
    reload() {
      return this.fetchPage();
    },
  },
  mounted() {
    this.fetchPage();
  },
  beforeUnmount() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
  mixins: [EditLabelMixin]
}
</script>

<style scoped>
.labels-total {
  font-weight: 600;
  color: #444;
}
.labels-hierarchy-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
}
.labels-hierarchy-filter__autocomplete {
  flex: 1 1 280px;
  max-width: 420px;
}
.labels-hierarchy-filter__checkbox {
  flex: 0 0 auto;
  margin-top: 0;
}
</style>
